import { useMutation, useQueryClient } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface BulkDeleteSkippedParent {
  parentId: string;
  name: string;
  email: string;
  reasons: Array<
    'active_enrollment' | 'outstanding_balance' | 'payment_in_progress'
  >;
}

export interface BulkDeleteParentResult {
  deletedCount: number;
  skipped: Array<BulkDeleteSkippedParent>;
}

export type BulkDeleteParentParams =
  | { parentIds: Array<string> }
  | { filters: { search?: string; tags?: string } };

export const bulkDeleteParentAPI = async (
  params: BulkDeleteParentParams,
): Promise<BulkDeleteParentResult> => {
  // Explicit ids are sent as the legacy raw string[] body so this remains
  // compatible with the production endpoint until the companion BE ships.
  const body =
    'parentIds' in params
      ? params.parentIds
      : {
          selectAllMatching: true,
          search: params.filters.search,
          tags: params.filters.tags,
        };
  const response = await baseAPI.delete<BulkDeleteParentResult>(
    '/parents/batch/all',
    {
      data: body,
    },
  );
  return response.data;
};

export const useBulkDeleteParent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: BulkDeleteParentParams) => bulkDeleteParentAPI(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [KEYS.PARENTS_LIST, KEYS.STUDIO],
      });
    },
  });
};
